import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';

import ReactCloudflareTurnstile from "react-cloudflare-turnstile";

import { config } from '../../config';
import './index.scss';
import Google from '../icons/Google';
import Github from '../icons/Github';
import Logo, { LogoLight } from '../Sidebar/Logo';
import { useGoogleLogin } from '@react-oauth/google';
import { useTheme } from '../../context/ThemeContext';
import { useWhiteLabel } from '../../context/WhiteLabelContext';
import Spinner from '../Global/Spinner';
import { fetchWithCSRF } from '../../utils/apiUtils';

const Signup = () => {
  const [signUpData, setSignUpData] = useState({
    name: '',
    email: '',
    password: ''
  });

  const [passwordError, setPasswordError] = useState(''); // New state for password error
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [isSubmit, setIsSubmit] = useState(false);
  const [buttonText, setButtonText] = useState('Continue');
  const [recaptchaToken, setCaptchaToken] = useState("");
  const [isGithubLoginSuccess, setIsGithubLoginSuccess] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const nameInputRef = useRef(null);
  // Autofocus the name field when the signup page loads
  useEffect(() => {
    nameInputRef.current?.focus();
  }, []);

  const { theme } = useTheme();
  const isDark = theme === 'dark' || theme === 'system';

  const {
    isGoogleAuthEnabled,
    isGithubAuthEnabled,
    getGithubClientId,
    canSignup,
    getAppName,
    loading: whiteLabelLoading,
    error: whiteLabelError,
    isUnregisteredDomain,
    domainInfo
  } = useWhiteLabel();


  // Password validation function
  const isPasswordStrong = (password) => {
    const lengthCheck = password.length >= 8;
    const uppercaseCheck = /[A-Z]/.test(password);
    const lowercaseCheck = /[a-z]/.test(password);
    const numberCheck = /\d/.test(password);
    const specialCharCheck = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    return lengthCheck && uppercaseCheck && lowercaseCheck && numberCheck && specialCharCheck;
  };

  // Handle pre-filled email from navigation
  useEffect(() => {
    if (location.state?.email) {
      setSignUpData(prevData => ({
        ...prevData,
        email: location.state.email
      }));
    }
  }, [location.state]);






  const handleChange = (e) => {
    const { name, value } = e.target;
    setSignUpData((prevData) => ({ ...prevData, [name]: value }));
    setMessage(null);
    setError(null);

    // Check password strength on change
    if (name === 'password') {
      if (!isPasswordStrong(value)) {
        setPasswordError('Password must be at least 8 characters long, contain an uppercase letter, a lowercase letter, a number, and a special character.');
      } else {
        setPasswordError('');
      }
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();

    if (passwordError) return; // Prevent submission if password is not strong

    // Don't submit before the Turnstile challenge resolves — otherwise the
    // backend rejects with 'Turnstile verification failed' and the user has no
    // idea why.
    if (!recaptchaToken) {
      setError('Please wait a moment for the security check to finish, then try again.');
      setMessage(null);
      return;
    }

    setIsSubmit(true);
    setButtonText('Signing up...');

    try {

      const response = await fetchWithCSRF(config.API_URI + '/v1/users/register', {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ ...signUpData, recaptchaToken })
      });

      if (!response.ok) {
        const errorData = await response.json();
        setError(errorData.message);
        setMessage(null);
        setIsSubmit(false);
        setButtonText('Continue');
      } else {
        setMessage("Thank you for signing up! We've sent a verification email to your inbox. Please check your email and click the link to verify your account.");
        setError(null);

        setTimeout(() => {
          navigate('/login');
        }, 2000);
      }
    } catch (error) {
      console.error('Error:', error.message);
      setError('An error occurred while signing up');
      setMessage(null);
      setIsSubmit(false);
      setButtonText('Continue');
    }
  };

  // Get the appropriate GitHub Client ID
  const getEffectiveGithubClientId = () => {
    const whiteLabelClientId = getGithubClientId();
    return whiteLabelClientId || config.GITHUB_CLIENT_ID;
  };

  const handleGitHubAuth = () => {
    setIsGithubLoginSuccess(true);

    const clientId = getEffectiveGithubClientId();
    // Use centralized OAuth callback domain for all white label subdomains
    const redirectUri = `${config.DOMAIN}/auth/oauth/callback`;
    const scope = 'user:email';
    const randomState = Math.random().toString(36).substring(2, 15);

    // Store the original domain to redirect back after authentication
    const originalDomain = window.location.origin;
    const stateData = {
      state: randomState,
      originalDomain: originalDomain,
      provider: 'github',
      action: 'signup'
    };

    localStorage.setItem('oauth_state_data', JSON.stringify(stateData));

    // Use a simple state parameter instead of JSON to avoid encoding issues
    const simpleState = `${randomState}|${btoa(originalDomain)}|github|signup`;

    const githubAuthUrl = `https://github.com/login/oauth/authorize?client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&scope=${scope}&state=${encodeURIComponent(simpleState)}`;

    window.location.href = githubAuthUrl;
  };

  const handleGoogleAuth = useGoogleLogin({
    onSuccess: async (response) => {
      try {
        // For white label domains, include the original domain
        const originalDomain = window.location.origin;

        // Call the unified backend endpoint
        const res = await fetchWithCSRF(config.API_URI + '/v1/users/auth/google', {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            token: response.access_token,
            action: 'signup',
            originalDomain: originalDomain
          }),
        });

        if (!res.ok) {
          const errorData = await res.json();
          throw new Error(errorData.message || 'Google authentication failed');
        }

        const data = await res.json();

        setMessage(data.message); // Displays either "Signup successful" or "Login successful"
        // setIsLoginSuccess(true);

        // Fetch user details using cookies (no need for Authorization header)
        const userResponse = await fetchWithCSRF(config.API_URI + '/v1/users/me', {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
          },
          credentials: 'include',
        });

        if (!userResponse.ok) {
          throw new Error('Failed to fetch user data');
        }

        const userData = await userResponse.json();
        const username = userData.user.username;

        setTimeout(() => {
          window.location.href = `/u/${username}/boards`;
        }, 2000);
      } catch (error) {
        console.error('Error during Google authentication:', error);
        setMessage(error.message || 'Google authentication failed. Please try again.');
        setTimeout(() => setMessage(''), 5000);
      }
    },
    onError: () => {
      setMessage('Google authentication failed. Please try again.');
      setTimeout(() => setMessage(''), 5000);
    },
  });





  // Show loading state while white label is loading
  if (whiteLabelLoading) {
    return (
      <>
        <Helmet>
          <title>Loading - {getAppName()}</title>
        </Helmet>
        <div className="login-container">
          <div className="login-box">
            <h1 className="login-logo">
              <Logo />
            </h1>
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <Spinner size={40} strokeWidth={3} />
              <p style={{ marginTop: '10px', color: '#666' }}>Loading...</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Show error message for unregistered domains
  if (isUnregisteredDomain && domainInfo) {
    return (
      <>
        <Helmet>
          <title>Domain Not Registered - {getAppName()}</title>
        </Helmet>
        <div className="login-container">
          <div className="login-box">
            <h1 className="login-logo">
              <Logo />
            </h1>
            <h2 className="login-heading">Domain Not Registered</h2>
            <p className="error-message" style={{ textAlign: 'center', margin: '20px 0' }}>
              The domain "{domainInfo.domain}" is not registered for access. Please check the URL or contact your administrator.
            </p>
          </div>
        </div>
      </>
    );
  }

  // Show error message if there's a white label error
  if (whiteLabelError) {
    return (
      <>
        <Helmet>
          <title>Error - {getAppName()}</title>
        </Helmet>
        <div className="login-container">
          <div className="login-box">
            <h1 className="login-logo">
              <Logo />
            </h1>
            <h2 className="login-heading">Service Unavailable</h2>
            <p className="error-message" style={{ textAlign: 'center', margin: '20px 0' }}>
              This service is currently unavailable. Please try again later or contact support.
            </p>
            <div className="login-links" style={{ textAlign: 'center' }}>
              <button
                onClick={() => window.location.reload()}
                className="login-link"
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#0966ff',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Try Again
              </button>
              <span> · </span>
              <Link to="/login" className="login-link">Back to Login</Link>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Check if signup is allowed
  if (!canSignup()) {
    return (
      <>
        <Helmet>
          <title>Signup - {getAppName()}</title>
        </Helmet>
        <div className="login-container">
          <div className="login-box">
            <h1 className="login-logo">
              <Logo />
            </h1>
            <h2 className="login-heading">Signup Not Available</h2>
            <p className="error-message">
              Public signup is not allowed on this domain. Please contact your administrator for an invitation.
            </p>
            <div className="login-links">
              <Link to="/login" className="login-link">Back to Login</Link>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Signup - {getAppName()}</title>
      </Helmet>

      <div className="login-container">
        <div className="login-box">
          <h1 className="login-logo">
            <Logo />
          </h1>
          <h2 className="login-heading">Sign up to continue</h2>
          <form className="signup-form" onSubmit={handleSignup}>
            <input
              type="text"
              name="name"
              placeholder="Name"
              value={signUpData.name}
              onChange={handleChange}
              className="login-input"
              required
              ref={nameInputRef}
            />
            <input
              type="email"
              name="email"
              placeholder="Email"
              value={signUpData.email}
              onChange={handleChange}
              className="login-input"
              required
            />
            <input
              type="password"
              name="password"
              placeholder="Password"
              value={signUpData.password}
              onChange={handleChange}
              className="login-input"
              required
            />
            {/* Display password error message if password is weak */}
            {passwordError && <p className="error-message">{passwordError}</p>}

            {message && <p className="success-message">{message}</p>}
            {error && <p className="error-message">{error}</p>}


            <div className="login-links">
              Already have an account?
              <span> · </span>
              <Link to="/login" className="login-link">Login</Link>
            </div>

            <p className="legal-message">
              By signing up, I accept the Zoobbe{' '}
              <Link to="/legal/terms-of-service" target="_blank" >
                Terms of Service
              </Link>{' '}
              and acknowledge the{' '}
              <Link to="/legal/privacy-policy" target="_blank" >
                Privacy Policy
              </Link>.
            </p>

            <ReactCloudflareTurnstile
              turnstileSiteKey={config.CLOUDFLARE_RECAPTCHA_SITE_KEY}
              callback={(token) => {
                setCaptchaToken(token)
              }}
            />

            <button type="submit" className="login-button" disabled={isSubmit || passwordError}>
              {buttonText}
            </button>
          </form>

          {/* Only show OAuth section if at least one provider is enabled */}
          {(isGoogleAuthEnabled() || isGithubAuthEnabled()) && (
            <>
              <p className="login-or">Or continue with:</p>
              <div className="login-social-buttons">
                {isGoogleAuthEnabled() && (
                  <button className="social-button google" onClick={() => handleGoogleAuth()}>
                    <Google /> Google
                  </button>
                )}
                {isGithubAuthEnabled() && (
                  <button className="social-button github" disabled={isGithubLoginSuccess} onClick={handleGitHubAuth}>
                    {
                      isGithubLoginSuccess ? (
                        <Spinner size={20} strokeWidth={5} />
                      ) : (
                        <><Github /> GitHub</>
                      )
                    }
                  </button>
                )}
              </div>
            </>
          )}

        </div>
      </div>
    </>
  );
};

export default Signup;
